import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Icon } from './icons.jsx';
import { hrefForPage, paths, toLocation } from '../src/router/app-paths.js';
import { resolveTenantId } from '../src/router/paths.js';
import {
  FEATURE_INTEGRATIONS,
  FEATURE_OBSERVABILITY,
  FEATURE_REPAIRS_MODULE,
  FEATURE_SUPPORT_MODULES,
  saIsFuturePage,
  saIsMockOnlyPage,
} from './mvp-config.jsx';
import { mapAuthUserToSidebarCard } from './services.jsx';
// Shell: sidebar + topbar + theme toggle + impersonate banner
const NAV_PLATAFORMA = [
  { id: 'dashboard',          label: 'Dashboard',              icon: 'dashboard' },
  { id: 'lojas',              label: 'Lojas',                  icon: 'store' },
  { id: 'planos',             label: 'Planos & Assinaturas',   icon: 'plans' },
  { id: 'billing',            label: 'Billing',                icon: 'cardCoin', alert: true },
  { id: 'notificacoes-ops',   label: 'Notificações',           icon: 'bell',     notifBadge: 'unread' },
  { id: 'config',             label: 'Configurações',          icon: 'settings' },
];

const NAV_SISTEMA = [
  { id: 'equipe',              label: 'Equipe e Permissões',   icon: 'shield',   superAdminOnly: true },
  { id: 'perfis',              label: 'Perfis de acesso',      icon: 'users',    superAdminOnly: true },
  { id: 'auditoria',           label: 'Auditoria',             icon: 'audit' },
  { id: 'planos-permissoes',   label: 'Planos & Permissões',   icon: 'layers' },
  { id: 'tech-spec',           label: 'Spec Técnica',          icon: 'terminal', devOnly: true },
];

const NAV_SUPORTE = [
  { id: 'tickets',        label: 'Tickets',               icon: 'messageCircle' },
  { id: 'knowledge-base', label: 'Base de Conhecimento',  icon: 'bookOpen'      },
  { id: 'support-reports',label: 'Relatórios de Suporte', icon: 'barChart'      },
];

const NAV_OPERACOES = [
  { id: 'integrations',    label: 'Integrations Center',  icon: 'link'     },
  { id: 'observabilidade', label: 'Observabilidade',      icon: 'activity' },
  { id: 'repairs',         label: 'Repairs & Diagnóstico',icon: 'wrench'   },
];

// ── Status Badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const config = {
    ativo:        { label: 'Ativo',        bg: 'var(--success-bg)', color: 'var(--success)' },
    trial:        { label: 'Trial',        bg: 'var(--info-bg)',    color: 'var(--info)'    },
    inadimplente: { label: 'Inadimplente', bg: 'var(--danger-bg)',  color: 'var(--danger)'  },
    suspenso:     { label: 'Suspenso',     bg: 'var(--bg-subtle)',  color: 'var(--text-muted)' },
  };
  const c = config[status] || config.ativo;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: c.bg, color: c.color, fontSize: 12, fontWeight: 600 }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.color }} />
      {c.label}
    </span>
  );
};

// ── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar = ({ route, activePage, onLogout, onProfile, authUser, showFuturePages, onShowFuturePagesChange, unreadNotifCount = 0 }) => {
  const [dropOpen, setDropOpen] = React.useState(false);
  const sidebarCard = authUser && typeof mapAuthUserToSidebarCard === 'function'
    ? mapAuthUserToSidebarCard(authUser)
    : null;

  React.useEffect(() => {
    if (!dropOpen) return;
    const handler = () => setDropOpen(false);
    setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropOpen]);

  // URL-derived activePage preferred; fall back to legacy route.page rules.
  const currentPage = activePage || route?.page;
  const isActive = (id) => {
    if (currentPage === id) return true;
    if (id === 'lojas' && (currentPage === 'loja' || currentPage === 'cockpit' || currentPage === 'nova-loja' || currentPage === 'lojas')) return true;
    if (id === 'equipe' && (currentPage === 'usuarios' || currentPage === 'equipe')) return true;
    return false;
  };

  const filterNav = (items) => items.filter(n => {
    if (n.id === 'tickets' || n.id === 'knowledge-base' || n.id === 'support-reports') {
      return FEATURE_SUPPORT_MODULES === true;
    }
    if (n.id === 'integrations') return FEATURE_INTEGRATIONS === true;
    if (n.id === 'observabilidade') return FEATURE_OBSERVABILITY === true;
    if (saIsMockOnlyPage(n.id)) return false;
    if (!showFuturePages && saIsFuturePage(n.id)) return false;
    return true;
  });

  const navPlataforma = filterNav(NAV_PLATAFORMA);
  const navSuporte = filterNav(NAV_SUPORTE);
  const navSistema = filterNav(NAV_SISTEMA);
  const navOperacoes = NAV_OPERACOES.map(n => ({
    ...n,
    prototype: (n.id === 'integrations' && FEATURE_INTEGRATIONS !== true) ||
      (n.id === 'observabilidade' && FEATURE_OBSERVABILITY !== true) ||
      (n.id === 'repairs' && FEATURE_REPAIRS_MODULE !== true),
    comingSoon: n.id === 'repairs' && FEATURE_REPAIRS_MODULE !== true,
  })).filter(n => showFuturePages || !saIsFuturePage(n.id));

  const NavItem = ({ n }) => (
    <NavLink
      key={n.id}
      to={hrefForPage(n.id)}
      end
      className={() => 'nav-item' + (isActive(n.id) ? ' active' : '')}
    >      <Icon name={n.icon} size={18} />
      <span>{n.label}</span>
      {n.prototype && <span style={{ fontSize: 9, fontWeight: 700, background: 'var(--bg-subtle)', color: 'var(--text-muted)', padding: '1px 5px', borderRadius: 4, marginLeft: 'auto', textTransform: 'uppercase' }}>{n.comingSoon ? 'Em breve' : 'Protótipo'}</span>}
      {n.count && <span className="count">{n.count}</span>}
      {n.alert && <span style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: '50%', background: 'var(--brand-red)', flexShrink: 0 }} />}
      {n.notifBadge === 'unread' && unreadNotifCount > 0 && (
        <span
          data-testid="nav-notif-badge"
          aria-label={`${unreadNotifCount} não lida${unreadNotifCount !== 1 ? 's' : ''}`}
          style={{ marginLeft: 'auto', width: 8, height: 8, borderRadius: '50%', background: 'var(--brand-red)', flexShrink: 0 }}
        />
      )}
      {n.superAdminOnly && <span style={{ fontSize: 9, fontWeight: 700, background: 'var(--danger-bg)', color: 'var(--danger)', padding: '1px 5px', borderRadius: 4, marginLeft: 'auto', textTransform: 'uppercase', letterSpacing: '0.04em' }}>SA</span>}
      {n.devOnly && <span style={{ fontSize: 9, fontWeight: 700, background: 'var(--bg-subtle)', color: 'var(--text-muted)', padding: '1px 5px', borderRadius: 4, marginLeft: 'auto', textTransform: 'uppercase', letterSpacing: '0.04em' }}>DEV</span>}
    </NavLink>
  );

  const handleToggleFuturePages = (e) => {
    e.stopPropagation();
    const next = !showFuturePages;
    if (typeof onShowFuturePagesChange === 'function') onShowFuturePagesChange(next);
  };

  return (
    <aside className="sidebar" style={{ overflowY: 'auto' }}>
      <div className="logo">
        <img src="assets/tezus-logo.svg" alt="Tezus" style={{ height: 26 }} />
        <span className="badge">Admin</span>
      </div>
      <div className="section-label">Plataforma</div>
      {navPlataforma.map(n => <NavItem key={n.id} n={n} />)}
      {navSuporte.length > 0 && <div className="section-label">Suporte</div>}
      {navSuporte.map(n => <NavItem key={n.id} n={n} />)}
      <div className="section-label">Sistema</div>
      {navSistema.map(n => <NavItem key={n.id} n={n} />)}
      {navOperacoes.length > 0 && <div className="section-label">Operações</div>}
      {navOperacoes.map(n => <NavItem key={n.id} n={n} />)}
      <div className="spacer" />
      <div className="user-card" style={{ position: 'relative' }}>
        <div className="avatar">{sidebarCard?.initials || '?'}</div>
        <div className="info">
          <div className="name">{sidebarCard?.label || 'Usuário'}</div>
          <div className="role">{sidebarCard?.role || '—'}</div>
        </div>
        <button
          className="icon-action"
          title="Opções"
          onClick={e => { e.stopPropagation(); setDropOpen(o => !o); }}
        >
          <Icon name="chevronDown" size={14} />
        </button>

        {dropOpen && (
          <div onMouseDown={e => e.stopPropagation()} style={{
            position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, right: 0,
            background: 'var(--bg-elevated)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
            overflow: 'hidden', zIndex: 200,
          }}>
            <button
              onClick={() => { setDropOpen(false); if (onProfile) onProfile(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--text)', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Icon name="users" size={14} />
              Meu perfil
            </button>
            <div style={{ height: 1, background: 'var(--border)' }} />
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={showFuturePages === true}
              onClick={handleToggleFuturePages}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--text)', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-subtle)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{
                width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                border: '1px solid var(--border-strong)',
                background: showFuturePages ? 'var(--brand-red)' : 'var(--bg-elevated)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 11, fontWeight: 700,
              }}>
                {showFuturePages ? '✓' : ''}
              </span>
              Mostrar páginas futuras
            </button>
            <div style={{ height: 1, background: 'var(--border)' }} />
            <button
              onClick={() => { setDropOpen(false); if (onLogout) onLogout(); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--danger)', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--danger-bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Icon name="x" size={14} />
              Sair
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

// ── Topbar ───────────────────────────────────────────────────────────────────
const Topbar = ({ route, pageLabel, breadcrumbs, theme, onToggleTheme, onBell, unreadCount = 0, hasSLAAlert = false, onSearch }) => {
  const PAGE_LABELS = {
    dashboard:           'Dashboard',
    lojas:               'Lojas',
    'nova-loja':         'Nova Loja',
    planos:              'Planos & Assinaturas',
    inadimplencia:       'Inadimplência',
    billing:             'Billing & Inadimplência',
    usuarios:            'Usuários',
    config:              'Configurações',
    auditoria:           'Auditoria',
    'planos-permissoes': 'Planos e Permissões',
    'tickets':           'Tickets',
    'knowledge-base':    'Base de Conhecimento',
    'support-reports':   'Relatórios de Suporte',
    integrations:        'Integrations Center',
    observabilidade:     'Observabilidade',
    repairs:             'Repairs & Diagnóstico',
    perfis:              'Perfis de acesso',
    equipe:              'Equipe e Permissões',
    'notificacoes-ops':  'Notificações Operacionais',
    'tech-spec':         'Especificação Técnica',
  };

  const page = route?.page;
  const crumbs = Array.isArray(breadcrumbs) && breadcrumbs.length
    ? breadcrumbs
    : null;
  const breadcrumbHref = (crumb) => crumb.to || (
    crumb.page === 'loja'
      ? paths.loja(resolveTenantId(crumb.store || route?.store))
      : hrefForPage(crumb.page)
  );

  return (
    <header className="topbar">
      {/* Breadcrumbs */}
      <nav className="crumbs">
        <Link to={paths.dashboard()}>Tezus</Link>
        <Icon name="chevronRight" size={11} className="sep" />
        {crumbs ? (
          crumbs.map((c, idx) => (
            <React.Fragment key={`${c.label}-${idx}`}>
              {idx > 0 && <Icon name="chevronRight" size={11} className="sep" />}
              {c.current || !c.page ? (
                <span className={c.current ? 'current' : undefined} style={c.current ? undefined : { color: 'var(--text-muted)' }}>
                  {c.label}
                </span>
              ) : (
                <Link to={toLocation(breadcrumbHref(c))}>{c.label}</Link>
              )}
            </React.Fragment>
          ))
        ) : page === 'cockpit' && route.store ? (
          <>
            <Link to={paths.lojas()}>Lojas</Link>
            <Icon name="chevronRight" size={11} className="sep" />
            <Link to={paths.loja(resolveTenantId(route.store))}>{route.store.name}</Link>
            <Icon name="chevronRight" size={11} className="sep" />
            <span className="current">Tenant Cockpit</span>
          </>
        ) : page === 'loja' && route.store ? (
          <>
            <Link to={paths.lojas()}>Lojas</Link>
            <Icon name="chevronRight" size={11} className="sep" />
            <span className="current">{route.store.name}</span>
          </>
        ) : page === 'equipe' || page === 'usuarios' ? (
          <>
            <span style={{ color: 'var(--text-muted)' }}>Sistema</span>
            <Icon name="chevronRight" size={11} className="sep" />
            <span className="current">{PAGE_LABELS[page] || pageLabel || 'Equipe'}</span>
          </>
        ) : (page === 'tickets' || page === 'knowledge-base' || page === 'support-reports') ? (
          <>
            <span style={{ color: 'var(--text-muted)' }}>Suporte</span>
            <Icon name="chevronRight" size={11} className="sep" />
            <span className="current">{PAGE_LABELS[page] || pageLabel || 'Suporte'}</span>
          </>
        ) : (
          <span className="current">{pageLabel || PAGE_LABELS[page] || 'Tezus'}</span>
        )}
      </nav>

      {/* Botão de busca global */}
      <button
        onClick={onSearch}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 12px', borderRadius: 8,
          border: '1px solid var(--border)', background: 'var(--bg-subtle)',
          color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13,
          transition: 'border-color 120ms, color 120ms',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
        title="Busca global (Cmd+K)"
      >
        <Icon name="search" size={14} />
        <span>Buscar…</span>
        <kbd style={{ fontSize: 10, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 5px', marginLeft: 4 }}>⌘K</kbd>
      </button>

      {/* Status chip */}
      <div className="chip">
        <span className="dot" />
        Sistemas operando
      </div>

      {/* Ações */}
      <div className="right">
        {typeof onBell === 'function' && (
          <button
            className="icon-btn"
            title="Notificações"
            onClick={onBell}
            style={{ position: 'relative' }}
          >
            <Icon name="bell" size={17} />
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: 6, right: 6,
                width: 7, height: 7, borderRadius: '50%',
                background: hasSLAAlert ? 'var(--brand-red)' : 'var(--brand-red)',
                border: '1.5px solid var(--bg-elevated)',
                animation: hasSLAAlert ? 'pulse 1.4s ease-in-out infinite' : 'none',
              }} />
            )}
          </button>
        )}
        <button
          className="icon-btn"
          title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
          onClick={onToggleTheme}
        >
          <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={16} />
        </button>
      </div>
    </header>
  );
};

// ── Banner de impersonação ───────────────────────────────────────────────────
const ImpersonateBanner = ({ store, onExit }) => {
  return (
    <div className="impersonate-banner">
      <div className="pulse-dot" />
      <span>Você está acessando <strong>{store.name}</strong> como <strong>proprietário</strong></span>
      <button className="btn ghost" onClick={onExit} style={{ marginLeft: 'auto', color: 'white', borderColor: 'rgba(255,255,255,0.3)' }}>
        Sair do modo impersonação
      </button>
    </div>
  );
};

// ── Modal de confirmação ─────────────────────────────────────────────────────
const ConfirmModal = ({ open, onClose, onConfirm, title, body, confirmLabel = 'Confirmar', danger = false, requireReason = false, minReasonLength = 5, confirmLoading = false }) => {
  const [reason, setReason] = React.useState('');
  React.useEffect(() => { if (!open) setReason(''); }, [open]);
  if (!open) return null;
  const canConfirm = !requireReason || reason.trim().length >= minReasonLength;
  const handleConfirm = () => {
    if (!canConfirm || confirmLoading) return;
    if (requireReason) onConfirm(reason.trim());
    else onConfirm();
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.5)', backdropFilter: 'blur(3px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 460, background: 'var(--bg-elevated)', borderRadius: 16, boxShadow: 'var(--shadow-lg)', overflow: 'hidden', animation: 'scaleIn 200ms ease' }}>
        <div style={{ padding: '24px 24px 20px', textAlign: requireReason ? 'left' : 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: danger ? 'var(--danger-bg)' : 'var(--info-bg)', margin: requireReason ? '0 0 16px' : '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name={danger ? 'alert' : 'check'} size={22} style={{ color: danger ? 'var(--danger)' : 'var(--info)' }} />
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{title}</h3>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{body}</div>
          {requireReason && (
            <div style={{ marginTop: 16, textAlign: 'left' }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Motivo (mín. {minReasonLength} caracteres)</label>
              <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="Descreva o motivo da alteração…" style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>Esta ação é registrada na auditoria.</div>
        </div>
        <div style={{ padding: '12px 20px', background: 'var(--bg-subtle)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose} disabled={confirmLoading}>Cancelar</button>
          <button className={'btn ' + (danger ? 'danger' : 'primary')} disabled={!canConfirm || confirmLoading} onClick={handleConfirm} style={{ opacity: canConfirm && !confirmLoading ? 1 : 0.5 }}>
            {confirmLoading ? 'Salvando…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message }) => {
  if (!message) return null;
  return (
    <div style={{ position: 'fixed', top: 16, right: 16, background: 'var(--text)', color: 'var(--bg-elevated)', padding: '12px 18px', borderRadius: 10, boxShadow: 'var(--shadow-lg)', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, fontWeight: 500, zIndex: 10000, animation: 'slideDown 200ms ease' }}>
      <Icon name="check" size={16} style={{ color: 'var(--success)' }} />
      {message}
    </div>
  );
};

export {
  ConfirmModal,
  ImpersonateBanner,
  Sidebar,
  StatusBadge,
  Toast,
  Topbar,
};
